/**
 * RoleTags - Display card gameplay roles as colored pill badges
 *
 * Shows up to 3 visible roles with icons and text.
 * Roles beyond 3 are hidden with a "+N" overflow indicator and tooltip.
 */
import type { ReactElement } from 'react';

import clsx from 'clsx';
import {
  GiCardDraw,
  GiCheckedShield,
  GiCrystalGrowth,
  GiDeathSkull,
  GiFeatheredWing,
  GiFireBomb,
  GiMagnifyingGlass,
  GiRainbowStar,
  GiRecycle,
} from 'react-icons/gi';

import { CARD_ROLE_INFO } from '@/lib/cardClassification';
import type { CardRoleId } from '@/types/card';

// ============================================================================
// Icon Mapping
// ============================================================================

const ROLE_ICONS: Record<CardRoleId, typeof GiCrystalGrowth> = {
  ramp: GiCrystalGrowth,
  cardDraw: GiCardDraw,
  removal: GiDeathSkull,
  boardWipe: GiFireBomb,
  protection: GiCheckedShield,
  recursion: GiRecycle,
  manaFixing: GiRainbowStar,
  tutor: GiMagnifyingGlass,
  evasion: GiFeatheredWing,
};

// ============================================================================
// Subcomponents
// ============================================================================

interface RoleTagProps {
  roleId: CardRoleId;
  /** Show only icon (compact mode) */
  iconOnly?: boolean;
  className?: string;
}

/**
 * Single role tag with icon and label
 */
function RoleTag({ roleId, iconOnly = false, className }: RoleTagProps): ReactElement {
  const info = CARD_ROLE_INFO[roleId];
  const Icon = ROLE_ICONS[roleId];

  return (
    <span
      className={clsx('inline-flex items-center gap-1 rounded-full px-1 py-0.5', info.bgColor, info.color, className)}
      title={info.label}
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      {!iconOnly && <span className="text-[10px] font-medium">{info.shortLabel}</span>}
    </span>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export interface RoleTagsProps {
  /** Array of active role IDs */
  roles: CardRoleId[];
  /** Maximum number of visible tags (default: 3, overflow shows "+N") */
  maxVisible?: number;
  /** Show only icons without text */
  iconOnly?: boolean;
  /** Additional className for the container */
  className?: string;
}

/**
 * Display card roles as colored pill badges.
 *
 * Shows icons for: Ramp, Card Draw, Removal, Board Wipe, Protection, Recursion.
 * Roles beyond maxVisible are hidden with a "+N" overflow indicator.
 */
export default function RoleTags({ roles, maxVisible = 3, iconOnly = false, className }: RoleTagsProps): ReactElement | null {
  if (roles.length === 0) {
    return null;
  }

  const visibleRoles = roles.slice(0, maxVisible);
  const overflowCount = roles.length - maxVisible;
  const overflowRoles = overflowCount > 0 ? roles.slice(maxVisible) : [];

  // Build tooltip for overflow
  const overflowTooltip = overflowRoles.map((id) => CARD_ROLE_INFO[id].label).join(', ');

  return (
    <div className={clsx('flex flex-wrap items-center gap-1', className)}>
      {visibleRoles.map((roleId) => (
        <RoleTag key={roleId} roleId={roleId} iconOnly={iconOnly} />
      ))}
      {overflowCount > 0 && (
        <span
          className="inline-flex items-center justify-center rounded-full bg-zinc-700 px-1.5 py-0.5 text-[10px] font-medium text-zinc-400"
          title={overflowTooltip}
        >
          +{overflowCount}
        </span>
      )}
    </div>
  );
}

// Export subcomponent for direct use
export { RoleTag };
